import { useState, useEffect, useRef, useMemo } from './wp';
import { loadIndex, searchIndex, formatEventDate } from './search-index';
import { SearchIcon, CloseIcon, ArrowIcon } from './icons';

const RECENT_KEY = 'tsaSearchRecent';
const RECENT_MAX = 5;

function readRecent() {
    try {
        const raw = window.localStorage.getItem(RECENT_KEY);
        return raw ? JSON.parse(raw).slice(0, RECENT_MAX) : [];
    } catch (e) {
        return [];
    }
}

function rememberRecent(query) {
    const term = query.trim();
    if (term.length < 2) {
        return;
    }

    try {
        const next = [term].concat(readRecent().filter((item) => item.toLowerCase() !== term.toLowerCase()));
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(next.slice(0, RECENT_MAX)));
    } catch (e) {
        /* storage unavailable — recents are a nicety, not a requirement */
    }
}

/**
 * Query string for the calendar app, which reads ?q= on load.
 *
 * @param {string} base
 * @param {string} query
 * @return {string}
 */
function calendarSearchUrl(base, query) {
    const separator = base.indexOf('?') === -1 ? '?' : '&';
    return base + separator + 'q=' + encodeURIComponent(query.trim());
}

function ResultRow({ item, active, onNavigate, id }) {
    const meta = [];

    if (item.k === 'event') {
        const date = formatEventDate(item.d);
        if (date) {
            meta.push(date);
        }
        if (item.ty) {
            meta.push(item.ty);
        }
    } else if (item.k === 'trip') {
        meta.push('Trip page');
    } else if (item.k === 'post') {
        meta.push('Story');
    }

    return (
        <li>
            <a
                id={id}
                className={'tsa-search__result' + (active ? ' is-active' : '')}
                href={item.u}
                role="option"
                aria-selected={active ? 'true' : 'false'}
                onClick={onNavigate}
            >
                {item.i ? (
                    <span className="tsa-search__thumb" style={{ backgroundImage: `url(${item.i})` }} aria-hidden="true" />
                ) : (
                    <span className="tsa-search__thumb tsa-search__thumb--blank" aria-hidden="true" />
                )}
                <span className="tsa-search__text">
                    <span className="tsa-search__title">{item.t}</span>
                    {meta.length > 0 && <span className="tsa-search__meta">{meta.join(' · ')}</span>}
                </span>
                <span className="tsa-search__go" aria-hidden="true"><ArrowIcon /></span>
            </a>
        </li>
    );
}

/**
 * The header search dropdown.
 *
 * @param {Object}   props
 * @param {boolean}  props.open
 * @param {Function} props.onClose
 * @param {Object}   props.config  TSA_CHROME.search
 * @param {string}   props.calendar Calendar (shop archive) URL
 */
export default function SearchPanel({ open, onClose, config, calendar }) {
    const [query, setQuery] = useState('');
    const [index, setIndex] = useState(null);
    const [status, setStatus] = useState('idle');
    const [active, setActive] = useState(-1);
    const [recent, setRecent] = useState(readRecent);
    const inputRef = useRef(null);

    /* Load the index the first time the panel opens, then reuse it. */
    useEffect(() => {
        if (!open || index || status === 'loading') {
            return;
        }

        setStatus('loading');
        loadIndex(config)
            .then((data) => {
                setIndex(data);
                setStatus('ready');
            })
            .catch(() => setStatus('error'));
    }, [open]);

    useEffect(() => {
        if (open) {
            setRecent(readRecent());
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        } else {
            setActive(-1);
        }
    }, [open]);

    const results = useMemo(() => searchIndex(index, query), [index, query]);

    const flat = useMemo(
        () => results.groups.reduce((all, group) => all.concat(group.items), []),
        [results]
    );

    useEffect(() => {
        setActive(-1);
    }, [query]);

    function submit(term) {
        const value = (term || query).trim();
        if (!value) {
            return;
        }
        rememberRecent(value);
        window.location.href = calendarSearchUrl(calendar, value);
    }

    function onKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (!flat.length) {
                return;
            }
            event.preventDefault();
            const direction = event.key === 'ArrowDown' ? 1 : -1;
            const next = (active + direction + flat.length + 1) % (flat.length + 1);
            setActive(next === flat.length ? -1 : next);
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            if (active >= 0 && flat[active]) {
                rememberRecent(query);
                window.location.href = flat[active].u;
                return;
            }
            submit();
        }
    }

    const quick = (index && index.quick) || [];
    const showEmpty = query.trim().length > 0 && status === 'ready' && results.total === 0;

    let position = -1;

    return (
        <div
            className={'tsa-search' + (open ? ' is-open' : '')}
            hidden={!open}
            role="dialog"
            aria-modal="false"
            aria-label="Search"
        >
            <div className="tsa-search__inner">
                <div className="tsa-search__bar" role="search">
                    <span className="tsa-search__icon" aria-hidden="true"><SearchIcon /></span>
                    <input
                        ref={inputRef}
                        type="text"
                        className="tsa-search__input"
                        placeholder={config.placeholder}
                        aria-label={config.placeholder}
                        role="combobox"
                        aria-expanded={flat.length > 0}
                        aria-controls="tsa-search-results"
                        aria-activedescendant={active >= 0 ? `tsa-search-result-${active}` : undefined}
                        autoComplete="off"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={onKeyDown}
                    />
                    {query && (
                        <button type="button" className="tsa-search__clear" onClick={() => { setQuery(''); inputRef.current && inputRef.current.focus(); }}>
                            <CloseIcon />
                            <span className="screen-reader-text">Clear search</span>
                        </button>
                    )}
                    <button type="button" className="tsa-search__submit" onClick={() => submit()} disabled={!query.trim()}>
                        Search
                    </button>
                    <button type="button" className="tsa-search__close" onClick={onClose} aria-label="Close search">
                        <CloseIcon />
                    </button>
                </div>

                <div className="tsa-search__body" id="tsa-search-results" role="listbox" aria-label="Search results">
                    {status === 'loading' && <p className="tsa-search__status">Loading adventures…</p>}
                    {status === 'error' && (
                        <p className="tsa-search__status">
                            Search is unavailable right now — <a href={calendar}>browse the calendar</a> instead.
                        </p>
                    )}

                    {!query.trim() && status === 'ready' && (
                        <div className="tsa-search__idle">
                            {recent.length > 0 && (
                                <div className="tsa-search__block">
                                    <h3>Recent searches</h3>
                                    <div className="tsa-search__chips">
                                        {recent.map((term) => (
                                            <button type="button" key={term} className="tsa-search__chip" onClick={() => setQuery(term)}>
                                                {term}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="tsa-search__block">
                                <h3>Browse by type</h3>
                                <div className="tsa-search__chips">
                                    {quick.map((link) => (
                                        <a className="tsa-search__chip" key={link.url} href={link.url}>
                                            {link.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {showEmpty && (
                        <p className="tsa-search__status">
                            Nothing matches “{query.trim()}”. Try a place, a trip name, or <a href={calendar}>see everything coming up</a>.
                        </p>
                    )}

                    {results.groups.map((group) => (
                        <div className="tsa-search__group" key={group.key}>
                            <h3>{group.title}</h3>
                            <ul>
                                {group.items.map((item) => {
                                    position += 1;
                                    const current = position;
                                    return (
                                        <ResultRow
                                            key={item.k + item.u}
                                            id={`tsa-search-result-${current}`}
                                            item={item}
                                            active={active === current}
                                            onNavigate={() => rememberRecent(query)}
                                        />
                                    );
                                })}
                            </ul>
                            {group.key === 'event' && group.matched > group.items.length && (
                                <button type="button" className="tsa-search__more" onClick={() => submit()}>
                                    See all {group.matched} departures on the calendar <ArrowIcon />
                                </button>
                            )}
                        </div>
                    ))}

                    {results.total > 0 && (
                        <button type="button" className="tsa-search__all" onClick={() => submit()}>
                            Search “{query.trim()}” on the calendar <ArrowIcon />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
